// Add types here to avoid potential dependency cycles
import type {
  AppointmentStatus,
  EnhancedAppointment,
  IAppointmentConfig,
} from '@bb/domain/dist/types';
import { AppointmentDoc } from '@bb/domain/dist/schemas';
import type {
  AgentType,
  ActionType,
} from '../../modals/AppointmentActionModal/types';
import type { CoopCancelReasonType } from '../../app/DashboardComponents/CoopCancelAppointmentComponent';

// #region AppointmentActionModal

// Use only the appointment properties we need since several appointment
// types are substituted into modal but are not assignable to each other
export type ActionModalAppointmentType = Pick<
  EnhancedAppointment,
  | 'appointmentConfig'
  | 'coop'
  | 'covid19RequestedAck'
  | 'covid19Signed'
  | 'id'
  | 'keepOriginalAppointment'
  | 'listingContactTimeChange'
  | 'listingId'
  | 'notify'
  | 'originalTime'
  | 'originalTimeEnd'
  | 'time'
  | 'timeEnd'
> & {
  listing: Pick<
    EnhancedAppointment['listing'],
    'line1' | 'line2' | 'id' | 'timezone'
  > & {
    thumbnail?: string;
  };
  status?: AppointmentStatus;
  orgHasCovidFlag?: boolean;
};

export type OpenAppointmentActionModalOptions = {
  appointment?: ActionModalAppointmentType;
  appointmentId?: string;
  agent: AgentType;
  action: ActionType;
  onConfirm?: () => void;
  onClose?: () => void;
  cancelFromTour?: (
    reason: CoopCancelReasonType,
    details: string,
    onConfirm?: () => void,
  ) => Promise<void>;
};

export type AppointmentActionModalContextType = {
  visible: boolean;
  loadingAppointment: boolean;
  loadingAppointmentConfig: boolean;
  openModal: (opts: OpenAppointmentActionModalOptions) => void;
  closeModal: () => void;
  onConfirm: () => void;
  onClose?: () => void;
  cancelFromTour?: (
    reason: CoopCancelReasonType,
    details: string,
    onConfirm?: () => void,
  ) => Promise<void>;
  agent: AgentType;
  action: ActionType;
  appointment: ActionModalAppointmentType;
  appointmentConfig: IAppointmentConfig;
  isMobileView: boolean;
};

// #endregion AppointmentActionModal

// #region FeedbackModal

export type FeedbackModalMode = 'record' | 'view';
export type FeedbackModalType = 'coop' | 'listing';

export type OpenFeedbackModalOptions = {
  modalType: FeedbackModalType;
  modalMode: FeedbackModalMode;
  listingId: string;
  appointmentId: string;
  onClose?: () => Promise<void>;
  onSubmit?: () => Promise<void>;
};

export type CloseFeedbackModalOptions = {
  resetModal?: boolean;
  fireSubmit?: boolean;
};

export type FeedbackModalContextType = {
  visible: boolean;
  openModal: (options: OpenFeedbackModalOptions) => void;
  closeModal: (options?: CloseFeedbackModalOptions) => void;
  modalType: FeedbackModalType;
  modalMode: FeedbackModalMode;
  listingId: string;
  appointmentId: string;
};

// #endregion FeedbackModal

// #region SelectUpgradePlan

export enum PlanName {
  AGENT = 'AGENT',
  BROKERAGE = 'BROKERAGE',
}

// #endregion SelectUpgradePlan
